import { useState } from 'react';
import { IoTrashOutline } from 'react-icons/io5';

import Error from '../Error';
import Loader from '../Loader';
import Pagination from '../Pagination';
import { tableBackgroundColor } from '../utils/tableBackgroundColor';
import CreateCategoryModal from './modals/CreateCategoryModal';
import EditCategoryModal from './modals/EditCategoryModal';

import {
	CategoryError,
	CategoryMessage,
} from '@/exceptions/category/category.enum';
import { useGetAllCategories } from '@/hooks/category/useCategory';
import useDeleteCategory from '@/hooks/category/useDeleteCategory';
import { ICategory } from '@/interfaces/entities/category';
import { notificationService } from '@/services/notification.service';

const CategoryTable = () => {
	const deleteCategory = useDeleteCategory();
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isCategory, setIsCategory] = useState<ICategory>();
	const [currentPage, setCurrentPage] = useState(1);
	const { categories, isLoading, isError, error, totalPages, isPending } =
		useGetAllCategories(currentPage);

	if (isLoading || isPending) {
		return <Loader />;
	}

	if (isError) {
		return (
			<Error message={error ? error.message : CategoryError.CATEGORY_ERROR} />
		);
	}

	const openEditModal = (category: ICategory) => {
		setIsCategory(category);
		setIsEditModalOpen(true);
	};

	const closeEditModal = () => {
		setIsEditModalOpen(false);
	};

	const openCreateModal = () => {
		setIsCreateModalOpen(true);
	};

	const closeCreateModal = () => {
		setIsCreateModalOpen(false);
	};

	const handleDelete = (id: number) => {
		notificationService.confirm(CategoryMessage.DELETE_MESSAGE, () => {
			deleteCategory.mutate(id);
		});
	};

	return (
		<div className="px-4 sm:px-6 lg:px-8 w-[85%] mb-10">
			<div className="sm:flex sm:items-center">
				<div className="flex justify-between font-bold sm:flex-auto">
					<h1
						data-test="h1-categories"
						className="text-base font-semibold leading-6 text-gray-900"
					>
						Categories
					</h1>
					<button
						className="text-indigo-600 hover:text-indigo-900"
						data-test="button-add-category"
						onClick={openCreateModal}
					>
						Add Category
					</button>
				</div>
			</div>
			{isCreateModalOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
					<div className="bg-white container max-w-lg p-3 rounded-xl shadow-lg border-2 border-black content-center">
						<CreateCategoryModal closeModal={closeCreateModal} />
					</div>
				</div>
			)}
			{isEditModalOpen && isCategory && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
					<div className="bg-white container max-w-lg p-3 rounded-xl shadow-lg border-2 border-black content-center">
						<EditCategoryModal
							closeModal={closeEditModal}
							initialState={isCategory}
						/>
					</div>
				</div>
			)}
			<div className="mt-8 flow-root">
				<div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
					<div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
						<table className="min-w-full divide-y divide-gray-300 border-red-300">
							<thead>
								<tr>
									<th
										scope="col"
										className="w-1/4  py-3.5 text-center text-sm font-semibold text-gray-900 "
										data-test="th-id"
									>
										ID
									</th>
									<th
										scope="col"
										className="w-1/4 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-name"
									>
										Name
									</th>
									<th
										scope="col"
										className="w-1/4 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-description"
									>
										Description
									</th>
									<th
										scope="col"
										className="w-1/4  py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-actions"
									>
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
								{categories.map((category: ICategory, index) => {
									return (
										<tr
											key={category.id}
											className={`${tableBackgroundColor(
												index,
											)} hover:bg-gray-200 min-h-8`}
										>
											<td
												data-test="td-id"
												className="text-center max-w-sm overflow-hidden text-ellipsis whitespace-nowrap py-4  text-sm font-medium text-gray-900   truncate"
											>
												{category.id}
											</td>
											<td
												data-test="td-name"
												className="text-center max-w-md overflow-hidden text-ellipsis whitespace-nowrap py-4 text-sm text-gray-500  truncate"
											>
												{category.name}
											</td>
											<td
												data-test="td-description"
												className="text-center max-w-md overflow-hidden text-ellipsis whitespace-nowrap py-4 text-sm text-gray-500  truncate"
											>
												{category.description}
											</td>
											<td
												data-test="edit-notification-button"
												className="flex justify-center items-center gap-6 whitespace-nowrap py-4  text-sm font-medium "
											>
												<button
													data-test="edit-category-button"
													className="text-indigo-600 hover:text-indigo-900"
													onClick={() => openEditModal(category)}
												>
													Edit
													<span className="sr-only">{category.id}</span>
												</button>

												<IoTrashOutline
													className="cursor-pointer"
													onClick={() => handleDelete(category.id)}
												/>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</div>
				<Pagination
					currentPage={currentPage}
					setCurrentPage={setCurrentPage}
					totalPages={totalPages as number}
				/>
			</div>
		</div>
	);
};

export default CategoryTable;
